import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { useField } from 'formik';

import { useDepartmentsBudgetsReportTableContext } from './context/DepartmentsBudgetsReportTableContext';
import { ROW_TYPE_BUDGET_LINE, ROW_TYPE_NET, ROW_TYPE_TOTAL } from './DepartmentsBudgetsReportTable';

import { formatFigure } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import EditIcon from 'Assets/icons/edit.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const DepartmentsBudgetsReportTableRow = ({
    lineId,
    group_name,
    description,
    workforce_type,
    budget,
    compareBudget,
    type = ROW_TYPE_BUDGET_LINE,
    show,
    expandable = false,
    expanded = false,
    level = 0,
    disableExpand = false,
    toggleHandler,
}) => {
    const { budgets, isOptionIncluded, showComments, editableComments, onCommentChange, markupCategories } = useDepartmentsBudgetsReportTableContext();

    const variance = useMemo(() => {
        if (budgets && budgets.length > 1) {
            return budget.total - (compareBudget?.total || 0);
        }
        return 0;
    }, [budget, compareBudget, budgets]);
    const isTotalRow = useMemo(() => type === ROW_TYPE_TOTAL, [type]);
    const isNetRow = useMemo(() => type === ROW_TYPE_NET, [type]);

    const clickHandler = () => {
        if (expandable && !disableExpand && typeof toggleHandler === 'function') {
            toggleHandler();
        }
    };

    if (!show) {
        return null;
    }

    return (
        <tr
            className={classNames('tw-text-xs', {
                'tw-cursor-pointer': expandable && !disableExpand,
                '!tw-bg-blue !tw-text-white': isTotalRow,
                '!tw-bg-white': !isTotalRow && !isNetRow,
                '!tw-bg-charcoal !tw-text-white': isNetRow,
            })}
            onClick={(e) => clickHandler()}
        >
            {/* Common columns */}
            <td
                className={classNames({
                    '!tw-bg-charcoal-15 tw-text-charcoal': level === 0 && !isTotalRow && !isNetRow,
                    '!tw-bg-charcoal-10 tw-text-charcoal': level === 1,
                })}
            >
                <div className={`tw-flex tw-items-center tw-gap-x-1.5 tw-pl-${3 * level}`}>
                    {expandable && (
                        <div className="tw-w-1.5">
                            {!disableExpand &&
                                (expanded ? (
                                    <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                ) : (
                                    <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                ))}
                        </div>
                    )}
                    <div>{group_name}</div>
                </div>
            </td>
            {isOptionIncluded('detailed_description') && <td>{description}</td>}
            {isOptionIncluded('workforce') && <td>{workforce_type}</td>}

            {/* Budgets columns */}
            {isOptionIncluded('cash') && <td className="inbud-numeric-cell">{formatFigure(budget.cash)}</td>}
            {isOptionIncluded('vik') && <td className="inbud-numeric-cell">{formatFigure(budget.vik)}</td>}
            {markupCategories &&
                markupCategories.length > 0 &&
                markupCategories.map((category) => (
                    <td key={category.id} className="inbud-numeric-cell">
                        {formatFigure(budget.markupCategories?.[category.id] || 0)}
                    </td>
                ))}
            <td className={classNames('inbud-numeric-cell', { '!tw-bg-[#D8D8D8]': type === ROW_TYPE_BUDGET_LINE })}>{formatFigure(budget.total)}</td>
            {budgets && budgets.length > 1 ? (
                <>
                    {isOptionIncluded('cash') && <td className="inbud-numeric-cell">{compareBudget && formatFigure(compareBudget?.cash)}</td>}
                    {isOptionIncluded('vik') && <td className="inbud-numeric-cell">{compareBudget && formatFigure(compareBudget?.vik)}</td>}
                    {markupCategories &&
                        markupCategories.length > 0 &&
                        markupCategories.map((category) => (
                            <td key={category.id} className="inbud-numeric-cell">
                                {compareBudget && formatFigure(compareBudget?.markupCategories?.[category.id] || 0)}
                            </td>
                        ))}
                    <td className={classNames('inbud-numeric-cell', { '!tw-bg-[#D8D8D8]': type === ROW_TYPE_BUDGET_LINE })}>
                        {compareBudget && formatFigure(compareBudget?.total)}
                    </td>
                </>
            ) : null}

            {/* Variance column */}
            {budgets && budgets.length > 1 && (
                <td className={classNames('inbud-numeric-cell', { '!tw-bg-[#D8D8D8]': type === ROW_TYPE_BUDGET_LINE })}>{formatFigure(variance)}</td>
            )}

            {/* Comment column */}
            {showComments && (
                <td className="tw-relative" onClick={(e) => e.stopPropagation()}>
                    <CommentCell editEnabled={editableComments} lineId={lineId} onCommentChange={onCommentChange} />
                </td>
            )}
        </tr>
    );
};

function CommentCell({ editEnabled, lineId, onCommentChange }) {
    const { comments, allComments, userType, editedComments } = useDepartmentsBudgetsReportTableContext();

    const lineComments = useMemo(() => allComments?.[lineId] || [], [allComments, lineId]);
    const editableValue = useMemo(() => comments?.[lineId] ?? editedComments?.[lineId] ?? '', [comments, editedComments, lineId]);
    const [editable, setEditable] = useState(false);

    // Display all comments with color coding
    const commentList = lineComments.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
            {lineComments.map((c) => (
                <span key={c.id} className={c.author_type === 'reviewer' ? 'tw-text-charcoal' : 'tw-text-red'}>
                    {c.author_name} ({c.author_type === 'reviewer' ? $t('Reviewer') : $t('User')}): {c.comment}
                </span>
            ))}
        </div>
    );

    if (!editEnabled) {
        return commentList || null;
    }

    if (editable) {
        if (onCommentChange) {
            return (
                <>
                    {commentList}
                    <CommentInputCallback budgetLineId={lineId} setEditable={setEditable} onCommentChange={onCommentChange} currentValue={editableValue} />
                </>
            );
        }
        return (
            <>
                {commentList}
                <CommentInputFormik budgetLineId={lineId} setEditable={setEditable} userType={userType} />
            </>
        );
    }

    return (
        <div onClick={() => setEditable(true)} className="tw-w-full tw-cursor-pointer tw-opacity-80 hover:tw-opacity-100">
            {commentList}
            <div className="tw-flex tw-items-center tw-gap-1 hover:tw-underline">
                {editableValue ? (
                    <>
                        <span className="tw-text-charcoal-50 tw-italic">{editableValue}</span>
                        <EditIcon className="tw-w-2.5 tw-h-2.5" />
                    </>
                ) : (
                    <>
                        <span className="tw-text-charcoal-50">{$t('Add comment')}</span>
                        <PlusIcon className="tw-w-2.5 tw-h-2.5" />
                    </>
                )}
            </div>
        </div>
    );
}

function CommentInputFormik({ budgetLineId, setEditable }) {
    const [field] = useField({ name: `comments.${budgetLineId}` });

    return (
        <input
            {...field}
            value={field.value ?? ''}
            type="text"
            className="tw-w-full tw-h-auto tw-px-1.5 tw-py-0.5 tw-text-xs tw-border tw-border-blue tw-rounded tw-outline-none"
            placeholder={$t('Enter comment...')}
            autoFocus
            onBlur={() => setEditable(false)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                    setEditable(false);
                }
            }}
        />
    );
}

function CommentInputCallback({ budgetLineId, setEditable, onCommentChange, currentValue }) {
    const [localValue, setLocalValue] = useState(currentValue ?? '');

    const handleBlur = () => {
        onCommentChange(budgetLineId, localValue);
        setEditable(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onCommentChange(budgetLineId, localValue);
            setEditable(false);
        } else if (e.key === 'Escape') {
            setLocalValue(currentValue ?? '');
            setEditable(false);
        }
    };

    return (
        <input
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="tw-w-full tw-h-auto tw-px-1.5 tw-py-0.5 tw-text-xs tw-border tw-border-blue tw-rounded tw-outline-none"
            placeholder={$t('Enter comment...')}
            autoFocus
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
        />
    );
}

export default DepartmentsBudgetsReportTableRow;
